import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { actions } from '../Users/redux/actions';
import Strings from '../../../Strings';

function OrgGroupDetail(props) {
  const { match, getOrgGroupDetails, editOrgGroup, getOrgGroupOrganizations, addOrganizationToOrgGroup, removeOrganizationFromOrgGroup, searchMaintainerCandidates, searchOrganizations, onNavigate } = props;
  const orgGroupId = match?.params?.orgGroupId;

  const [orgGroup, setOrgGroup] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedOrgGroup, setEditedOrgGroup] = React.useState({});

  // Organizations state
  const [organizations, setOrganizations] = React.useState([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = React.useState(false);
  const [organizationSearchTerm, setOrganizationSearchTerm] = React.useState('');
  const [organizationSearchResults, setOrganizationSearchResults] = React.useState([]);
  const [isSearchingOrganizations, setIsSearchingOrganizations] = React.useState(false);
  const [selectedOrganization, setSelectedOrganization] = React.useState(null);
  const [showOrganizationDropdown, setShowOrganizationDropdown] = React.useState(false);

  // Maintainer state
  const [maintainerSearchTerm, setMaintainerSearchTerm] = React.useState('');
  const [maintainerSearchResults, setMaintainerSearchResults] = React.useState([]);
  const [isSearchingMaintainers, setIsSearchingMaintainers] = React.useState(false);
  const [selectedMaintainer, setSelectedMaintainer] = React.useState(null);
  const [showMaintainerDropdown, setShowMaintainerDropdown] = React.useState(false);

  const loadOrgGroup = React.useCallback(() => {
    if (!orgGroupId || !getOrgGroupDetails) return;

    setIsLoading(true);
    setError(null);
    getOrgGroupDetails(orgGroupId)
      .then(resp => {
        const groupData = resp?.response;
        if (groupData) {
          setOrgGroup(groupData);
        } else {
          setError('Org Group not found');
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Failed to load org group:', err);
        setError('Failed to load org group');
        setIsLoading(false);
      });
  }, [orgGroupId, getOrgGroupDetails]);

  const loadOrganizations = React.useCallback(() => {
    if (!orgGroupId || !getOrgGroupOrganizations) return;

    setIsLoadingOrganizations(true);
    getOrgGroupOrganizations(orgGroupId)
      .then(resp => {
        const orgsData = resp?.response || [];
        setOrganizations(Array.isArray(orgsData) ? orgsData : []);
        setIsLoadingOrganizations(false);
      })
      .catch(err => {
        console.error('Failed to load organizations:', err);
        setIsLoadingOrganizations(false);
      });
  }, [orgGroupId, getOrgGroupOrganizations]);

  React.useEffect(() => {
    loadOrgGroup();
    loadOrganizations();
  }, [loadOrgGroup, loadOrganizations]);

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      setEditedOrgGroup({});
    } else {
      setIsEditing(true);
      setEditedOrgGroup({ ...orgGroup });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedOrgGroup(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!editOrgGroup || !orgGroup) return;

    setIsLoading(true);
    setError(null);

    try {
      await editOrgGroup(orgGroupId, {
        name: editedOrgGroup.name || orgGroup.name,
        description: editedOrgGroup.description || orgGroup.description,
      });
      loadOrgGroup();
      setIsEditing(false);
      setEditedOrgGroup({});
    } catch (err) {
      console.error('Failed to update org group:', err);
      setError('Failed to update org group');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('/admin/org-groups');
    }
  };

  // Organization search handlers
  const handleOrganizationSearchChange = (e) => {
    const term = e.target.value;
    setOrganizationSearchTerm(term);
    setSelectedOrganization(null);

    if (term.length >= 2 && searchOrganizations) {
      setIsSearchingOrganizations(true);
      searchOrganizations(term)
        .then(resp => {
          const orgs = resp?.response?.data || [];
          setOrganizationSearchResults(orgs);
          setShowOrganizationDropdown(orgs.length > 0);
          setIsSearchingOrganizations(false);
        })
        .catch(() => {
          setIsSearchingOrganizations(false);
          setOrganizationSearchResults([]);
          setShowOrganizationDropdown(false);
        });
    } else {
      setOrganizationSearchResults([]);
      setShowOrganizationDropdown(false);
    }
  };

  const handleSelectOrganization = (org) => {
    setSelectedOrganization(org);
    setOrganizationSearchTerm(org.name);
    setShowOrganizationDropdown(false);
  };

  const handleAddOrganization = async () => {
    if (!selectedOrganization || !addOrganizationToOrgGroup) return;

    setIsLoadingOrganizations(true);
    try {
      await addOrganizationToOrgGroup(orgGroupId, selectedOrganization.id);
      loadOrganizations();
      setOrganizationSearchTerm('');
      setSelectedOrganization(null);
      setOrganizationSearchResults([]);
    } catch (err) {
      console.error('Failed to add organization:', err);
      setIsLoadingOrganizations(false);
    }
  };

  const handleRemoveOrganization = async (organizationId) => {
    if (!removeOrganizationFromOrgGroup) return;
    if (!window.confirm('Are you sure you want to remove this organization from the group?')) return;

    setIsLoadingOrganizations(true);
    try {
      await removeOrganizationFromOrgGroup(orgGroupId, organizationId);
      loadOrganizations();
    } catch (err) {
      console.error('Failed to remove organization:', err);
      setIsLoadingOrganizations(false);
    }
  };

  // Maintainer search handlers - searches only users from controlling org with org_superuser role
  const handleMaintainerSearchChange = (e) => {
    const term = e.target.value;
    setMaintainerSearchTerm(term);
    setSelectedMaintainer(null);

    if (term.length >= 2 && searchMaintainerCandidates && orgGroupId) {
      setIsSearchingMaintainers(true);
      searchMaintainerCandidates(orgGroupId, term)
        .then(resp => {
          const users = resp?.response || [];
          setMaintainerSearchResults(users);
          setShowMaintainerDropdown(users.length > 0);
          setIsSearchingMaintainers(false);
        })
        .catch(() => {
          setIsSearchingMaintainers(false);
          setMaintainerSearchResults([]);
          setShowMaintainerDropdown(false);
        });
    } else {
      setMaintainerSearchResults([]);
      setShowMaintainerDropdown(false);
    }
  };

  const handleSelectMaintainer = (user) => {
    setSelectedMaintainer(user);
    setMaintainerSearchTerm(user.email || user.username);
    setShowMaintainerDropdown(false);
  };

  const handleAddMaintainer = async () => {
    if (!selectedMaintainer || !editOrgGroup || !orgGroup) return;

    setIsLoading(true);
    try {
      const currentMaintainerIds = orgGroup.maintainers?.map(m => m.id) || [];
      const updatedMaintainerIds = [...currentMaintainerIds, selectedMaintainer.id];
      await editOrgGroup(orgGroupId, { maintainers: updatedMaintainerIds });
      loadOrgGroup();
      setMaintainerSearchTerm('');
      setSelectedMaintainer(null);
      setMaintainerSearchResults([]);
    } catch (err) {
      console.error('Failed to add maintainer:', err);
      setIsLoading(false);
    }
  };

  const handleRemoveMaintainer = async (maintainerId) => {
    if (!editOrgGroup || !orgGroup) return;
    if (!window.confirm('Are you sure you want to remove this maintainer?')) return;

    setIsLoading(true);
    try {
      const currentMaintainerIds = orgGroup.maintainers?.map(m => m.id) || [];
      const updatedMaintainerIds = currentMaintainerIds.filter(id => id !== maintainerId);
      await editOrgGroup(orgGroupId, { maintainers: updatedMaintainerIds });
      loadOrgGroup();
    } catch (err) {
      console.error('Failed to remove maintainer:', err);
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (isLoading && !orgGroup) {
    return (
      <div className="org-group-detail-page" style={{ padding: '20px' }}>
        <div className="loading">{Strings.loading || 'Loading org group details...'}</div>
      </div>
    );
  }

  if (error && !orgGroup) {
    return (
      <div className="org-group-detail-page" style={{ padding: '20px' }}>
        <div className="error-message" style={{ color: 'red' }}>
          <p>{error}</p>
        </div>
        <button className="btn-back" onClick={handleBack}>
          {Strings.backToOrgGroups || 'Back to Org Groups'}
        </button>
      </div>
    );
  }

  if (!orgGroup) {
    return (
      <div className="org-group-detail-page" style={{ padding: '20px' }}>
        <div className="error-message">
          <p>{Strings.orgGroupNotFound || 'Org Group not found'}</p>
        </div>
        <button className="btn-back" onClick={handleBack}>
          {Strings.backToOrgGroups || 'Back to Org Groups'}
        </button>
      </div>
    );
  }

  const displayGroup = isEditing ? { ...orgGroup, ...editedOrgGroup } : orgGroup;

  return (
    <div className="org-group-detail-page" style={{ padding: '20px' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <button className="btn-back" onClick={handleBack} style={{ marginRight: '20px', cursor: 'pointer' }}>
            ← {Strings.backToOrgGroups || 'Back to Org Groups'}
          </button>
          <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {orgGroup.name}
          </span>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <button className="btn-edit" onClick={handleEditToggle} style={{ padding: '8px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', lineHeight: 1 }}>
              {Strings.editOrgGroup || 'Edit Org Group'}
            </button>
          ) : (
            <>
              <button className="btn-cancel" onClick={handleEditToggle} disabled={isLoading} style={{ padding: '8px 16px', marginRight: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', lineHeight: 1 }}>
                {Strings.cancel || 'Cancel'}
              </button>
              <button className="btn-save" onClick={handleSave} disabled={isLoading} style={{ padding: '8px 16px', cursor: 'pointer', backgroundColor: '#4CAF50', color: 'white', border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', lineHeight: 1 }}>
                {isLoading ? (Strings.saving || 'Saving...') : (Strings.saveChanges || 'Save Changes')}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
          <p>{error}</p>
        </div>
      )}

      <div className="org-group-detail-content">
        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.groupInformation || 'Group Information'}</h2>
          <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '15px' }}>
            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.id || 'ID'}</label>
              <div className="field-value">{orgGroup.id}</div>
            </div>

            {orgGroup.uuid && (
              <div className="detail-field">
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.uuid || 'UUID'}</label>
                <div className="field-value">{orgGroup.uuid}</div>
              </div>
            )}

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.name || 'Name'}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={displayGroup.name || ''}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{orgGroup.name}</div>
              )}
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.controllingOrg || 'Controlling Organization'}</label>
              <div className="field-value">
                {orgGroup.controlling_org ? (
                  <span>{orgGroup.controlling_org.name}</span>
                ) : (
                  <span style={{ color: '#6c757d' }}>{Strings.noControllingOrg || 'No controlling organization'}</span>
                )}
              </div>
            </div>

            <div className="detail-field" style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.description || 'Description'}</label>
              {isEditing ? (
                <textarea
                  name="description"
                  value={displayGroup.description || ''}
                  onChange={handleInputChange}
                  rows={3}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{orgGroup.description || 'N/A'}</div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.maintainers || 'Maintainers'}</h2>
          <div className="maintainers-list" style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {orgGroup.maintainers && orgGroup.maintainers.length > 0 ? (
              orgGroup.maintainers.map(maintainer => (
                <span
                  key={maintainer.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#e9ecef',
                    borderRadius: '10px',
                    padding: '4px 10px',
                    fontSize: '13px',
                  }}
                >
                  {maintainer.first_name} {maintainer.last_name}
                  {maintainer.email && <span style={{ color: '#666', marginLeft: '4px' }}>&lt;{maintainer.email}&gt;</span>}
                  <span
                    onClick={() => handleRemoveMaintainer(maintainer.id)}
                    style={{ marginLeft: '6px', cursor: 'pointer', color: '#6c757d', fontSize: '14px' }}
                    title="Remove maintainer"
                  >
                    ×
                  </span>
                </span>
              ))
            ) : (
              <span style={{ color: '#6c757d' }}>{Strings.noMaintainersAssigned || 'No maintainers assigned'}</span>
            )}
          </div>
          <div style={{ marginTop: '10px', position: 'relative', maxWidth: '500px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  id="maintainer-search-input"
                  type="text"
                  placeholder={Strings.searchMaintainerCandidates || 'Search org superusers...'}
                  value={maintainerSearchTerm}
                  onChange={handleMaintainerSearchChange}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                {isSearchingMaintainers && <div style={{ position: 'absolute', top: '100%', left: 0, padding: '8px', backgroundColor: 'white', border: '1px solid #ccc' }}>Searching...</div>}
                {showMaintainerDropdown && maintainerSearchResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto', zIndex: 100, borderRadius: '4px' }}>
                    {maintainerSearchResults.map(user => (
                      <div
                        key={user.uuid || user.id}
                        onClick={() => handleSelectMaintainer(user)}
                        style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                        onMouseEnter={e => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                      >
                        <div>{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{user.email || user.username}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleAddMaintainer}
                disabled={!selectedMaintainer || isLoading}
                style={{ padding: '8px 16px', cursor: selectedMaintainer ? 'pointer' : 'not-allowed', backgroundColor: selectedMaintainer ? '#4CAF50' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >
                + {Strings.add || 'Add'}
              </button>
            </div>
          </div>
        </div>

        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.organizations || 'Organizations'}</h2>
          <div className="organizations-list" style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {isLoadingOrganizations && organizations.length === 0 ? (
              <span>{Strings.loadingOrganizations || 'Loading organizations...'}</span>
            ) : organizations.length > 0 ? (
              organizations.map(org => (
                <span
                  key={org.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#e9ecef',
                    borderRadius: '10px',
                    padding: '4px 10px',
                    fontSize: '13px',
                  }}
                >
                  {org.name}
                  <span
                    onClick={() => handleRemoveOrganization(org.id)}
                    style={{ marginLeft: '6px', cursor: 'pointer', color: '#6c757d', fontSize: '14px' }}
                    title="Remove organization"
                  >
                    ×
                  </span>
                </span>
              ))
            ) : (
              <span style={{ color: '#6c757d' }}>{Strings.noOrganizationsAssigned || 'No organizations assigned'}</span>
            )}
          </div>
          <div style={{ marginTop: '10px', position: 'relative', maxWidth: '500px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  id="organization-search-input"
                  type="text"
                  placeholder={Strings.searchOrganizationsByName || 'Search organizations by name'}
                  value={organizationSearchTerm}
                  onChange={handleOrganizationSearchChange}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                {isSearchingOrganizations && <div style={{ position: 'absolute', top: '100%', left: 0, padding: '8px', backgroundColor: 'white', border: '1px solid #ccc' }}>Searching...</div>}
                {showOrganizationDropdown && organizationSearchResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto', zIndex: 100, borderRadius: '4px' }}>
                    {organizationSearchResults.map(org => (
                      <div
                        key={org.id || org.uuid}
                        onClick={() => handleSelectOrganization(org)}
                        style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                        onMouseEnter={e => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                      >
                        <div>{org.name}</div>
                        {org.unguessable_id && <div style={{ fontSize: '12px', color: '#666' }}>ID: {org.unguessable_id}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleAddOrganization}
                disabled={!selectedOrganization || isLoadingOrganizations}
                style={{ padding: '8px 16px', cursor: selectedOrganization ? 'pointer' : 'not-allowed', backgroundColor: selectedOrganization ? '#4CAF50' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >
                + {Strings.add || 'Add'}
              </button>
            </div>
          </div>
        </div>

        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.statistics || 'Statistics'}</h2>
          <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginTop: '15px' }}>
            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.organizations || 'Organizations'}</label>
              <div className="field-value">{orgGroup.organization_count || 0}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.users || 'Users'}</label>
              <div className="field-value">{orgGroup.user_count || 0}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.status || 'Status'}</label>
              <div className="field-value">
                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: orgGroup.is_active !== false ? '#4CAF50' : '#f44336', color: 'white' }}>
                  {orgGroup.is_active !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.timestamps || 'Timestamps'}</h2>
          <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '15px' }}>
            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.createdAt || 'Created At'}</label>
              <div className="field-value">{formatDate(orgGroup.created_at)}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.updatedAt || 'Updated At'}</label>
              <div className="field-value">{formatDate(orgGroup.updated_at)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapDispatchToProps = dispatch => ({
  getOrgGroupDetails: orgGroupId => dispatch(actions.getOrgGroupDetails(orgGroupId)),
  editOrgGroup: (orgGroupId, data) => dispatch(actions.editOrgGroup(orgGroupId, data)),
  getOrgGroupOrganizations: orgGroupId => dispatch(actions.getOrgGroupOrganizations(orgGroupId)),
  addOrganizationToOrgGroup: (orgGroupId, organizationId) => dispatch(actions.addOrganizationToOrgGroup(orgGroupId, organizationId)),
  removeOrganizationFromOrgGroup: (orgGroupId, organizationId) => dispatch(actions.removeOrganizationFromOrgGroup(orgGroupId, organizationId)),
  searchMaintainerCandidates: (orgGroupId, searchTerm) => dispatch(actions.searchMaintainerCandidates(orgGroupId, searchTerm)),
  searchOrganizations: searchTerm => dispatch(actions.searchOrganizations(searchTerm)),
  onNavigate: path => dispatch(push(path)),
});

export default connect(null, mapDispatchToProps)(OrgGroupDetail);
